import { type JSX, useEffect, useState } from 'react';
import { generatePath, useLocation, useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { UCRM_SUCCESS_MESSAGE_USE_CASE } from 'Config/Text';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { TPRM_ROLES, UCRM_ROLES } from 'Helpers/UserUtils';
import { ThirdPartyService } from 'Models/TPRM';
import { BusinessForNonUcrmUser, BusinessResponse, UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';
import { SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_ID, SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_NAME } from 'Pages/UCRM/UseCaseDashboard/UseCaseDashboard';

import { ManageUseCaseFormFields } from './ManageUseCaseFormFields/ManageUseCaseFormFields';

interface UrlPathParams {
    thirdPartyId: string;
    serviceId: string;
}

/** @param tprmModuleApi The API for the TPRM module, used to get the TPRM third-party services that the use case can be associated with. Not to be confused with `ucrmApi`. */
export interface ManageUseCaseProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    authHandler: AuthHandler;
    tprmModuleApi: ClientTPRMApi;
    ucrmApi: ClientUcrmApi;
}

/** Allows the user to update or delete an existing use case. Note that use cases are created via the `UcrmIntake` component, not via this component. */
export const ManageUseCase = (props: ManageUseCaseProps): JSX.Element => {
    const { thirdPartyId, serviceId } = useParams<keyof UrlPathParams>() as UrlPathParams;

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    /** Present in the URL if and only if the user navigated here from the Use Case Dashboard page. */
    const useCaseDashboardOriginUseCaseId = searchParams.get(SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_ID);
    /** Present in the URL if and only if the user navigated here from the Use Case Dashboard page. */
    const useCaseDashboardOriginUseCaseName = searchParams.get(SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_NAME);

    const userRoles = props.authHandler.getUserRoles();
    const userHasFullUcrmAccess = userHasAuthorizedRole(userRoles, UCRM_ROLES);
    /** Mapping a use case to TPRM third-party services requires reading TPRM services, which requires a TPRM role in addition to a UCRM role. */
    const userCanMapTprmServices = userHasFullUcrmAccess && userHasAuthorizedRole(userRoles, TPRM_ROLES);

    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);
    const [useCase, setUseCase] = useState<UseCase | UseCaseForNonUcrmUser>();
    const [business, setBusiness] = useState<BusinessResponse | BusinessForNonUcrmUser>();
    const [toastContent, setToastContent] = useState<{ variant: 'success' | 'failure'; text: string }>();
    /** `null` if the TPRM services can't be associated with the use case (i.e., the user lacks access to TPRM, or the request for them failed), in which case the form omits the associations. */
    const [tprmServices, setTprmServices] = useState<ThirdPartyService[] | null>();

    useEffect(() => {
        const getBusiness = async (): Promise<void> => {
            try {
                setBusiness((userHasFullUcrmAccess ? await props.ucrmApi.getBusinessDetails(thirdPartyId) : await props.ucrmApi.getBusinessDetailsForNonUcrmUser(thirdPartyId)).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        const getUseCase = async (): Promise<void> => {
            try {
                setUseCase((userHasFullUcrmAccess ? await props.ucrmApi.getUseCaseDetails(thirdPartyId, serviceId) : await props.ucrmApi.getUseCaseDetailsForNonUcrmUser(thirdPartyId, serviceId)).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        // Failing to get TPRM services does not prevent the user from managing the rest of the use case.
        const getTprmServices = async (): Promise<void> => {
            if (!userCanMapTprmServices) {
                setTprmServices(null);
                return;
            }

            try {
                setTprmServices((await props.tprmModuleApi.getThirdPartyServices()).data);
            } catch (error) {
                setTprmServices(null);
                setToastContent({ variant: 'failure', text: error.message });
            }
        };

        getBusiness();
        getUseCase();
        getTprmServices();
    }, [serviceId, thirdPartyId, props.ucrmApi, props.tprmModuleApi, userCanMapTprmServices, userHasFullUcrmAccess]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (useCase === undefined || business === undefined || tprmServices === undefined) {
        return <Placeholder />;
    }

    const headerTitle = 'Manage Use Case';

    return (
        <>
            {toastContent && <TextToast clearToast={() => setToastContent(undefined)} {...toastContent} />}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(UCRM_BUSINESSES_AND_USE_CASES_LISTING)}>Businesses and Use Cases</BreadcrumbLink>
                        {useCaseDashboardOriginUseCaseId && useCaseDashboardOriginUseCaseName && <BreadcrumbLink link={generatePath(UCRM_USE_CASE_DASHBOARD, { third_party_id: thirdPartyId, service_id: useCaseDashboardOriginUseCaseId })}>{`${business.name} - ${useCaseDashboardOriginUseCaseName}`}</BreadcrumbLink>}
                        <BreadcrumbText>{headerTitle}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={headerTitle}
                body={[
                    {
                        content: <ManageUseCaseFormFields artificialIntelligenceApi={props.artificialIntelligenceApi} userHasFullUcrmAccess={userHasFullUcrmAccess} business={business} useCase={useCase} ucrmApi={props.ucrmApi} tprmServices={tprmServices ?? undefined} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmitFailed={(text) => setToastContent({ variant: 'failure', text })} onFormSubmittedSuccessfully={async () => setToastContent({ variant: 'success', text: UCRM_SUCCESS_MESSAGE_USE_CASE })} />,
                    },
                ]}
            />
        </>
    );
};
